import React, { useState } from 'react';
import { Alert as AntAlert, Card as AntCard, List, theme } from 'antd';

type ContainerProps = React.PropsWithChildren<{
  /** Bootstrap utility classes. */
  addClass?: string;
}>;

const Container = ({ addClass, children }: ContainerProps) => {
  const { token } = theme.useToken();
  return (
    <div className={addClass} style={{ width: '100%', marginInline: 'auto', paddingInline: token.paddingSM }}>
      {children}
    </div>
  );
};

type CardProps = React.PropsWithChildren<{
  /** Bootstrap column class of the wrapper (`col-12`). */
  width?: string;
  title?: React.ReactNode;
}>;

const Card = ({ width = 'col-12', title, children }: CardProps) => {
  const { token } = theme.useToken();
  return (
    <div className={width}>
      <AntCard title={title} style={{ marginBottom: token.marginLG }}>
        {children}
      </AntCard>
    </div>
  );
};

/** antd's List.Item is a flex row, so `col` / `col-auto` children keep their layout. */
const ListGroup = ({ children }: React.PropsWithChildren) => {
  const { token } = theme.useToken();
  return (
    // Through `dataSource`, so antd renders the `<li>` items inside its `<ul>`; plain children land beside it.
    <List
      dataSource={React.Children.toArray(children)}
      renderItem={(item) => item}
      style={{ marginBlock: -token.paddingContentVertical }}
    />
  );
};

const ListGroupItem = ({ children }: React.PropsWithChildren) => <List.Item>{children}</List.Item>;

export type AlertContent = {
  /** A string, not a node: dismissal is tracked per text, compared by value. */
  text?: string;
};

type AlertProps = {
  content?: AlertContent | null;
  modal?: boolean;
};

const Alert = ({ content, modal = false }: AlertProps) => {
  const { token } = theme.useToken();
  const [dismissed, setDismissed] = useState(false);
  const [dismissibleText, setDismissibleText] = useState(content?.text);
  if (content?.text !== dismissibleText) {
    setDismissibleText(content?.text);
    setDismissed(false);
  }
  if (!content?.text || dismissed) return null;

  return (
    <AntAlert
      type="error"
      message={content.text}
      closable={!modal}
      onClose={() => setDismissed(true)}
      style={modal ? { borderRadius: 0, marginBottom: token.marginSM } : { marginBottom: token.marginLG }}
    />
  );
};

export { Container, Card, ListGroup, ListGroupItem, Alert };
